import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Download, Upload, FileSpreadsheet, FileText } from 'lucide-react'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { UploadConfirmModal, type UploadRow } from './UploadConfirmModal'
import {
  flattenIds,
  unflattenIds,
  generateId,
  exportToCSV,
  exportToExcel,
  parseCSV,
  parseExcel,
  validateRows,
  type FieldTransformer,
} from '../utils/importExport'

interface ImportExportButtonsProps<T extends { id: string; name?: string }> {
  /** The current list data to export. Ignored when `loadAll` is given. */
  data?: T[]
  /**
   * Fetches the whole list, for a page that only holds one page of it. Used
   * for the download and for telling new rows from updated ones on upload, so
   * a paged list still exports and diffs against everything.
   */
  loadAll?: () => Promise<T[]>
  /** Base filename (without extension) */
  filename: string
  /**
   * Fields whose values should be wrapped in { id: string } when uploading.
   * On download these are already flattened automatically if they were { id: ... } objects.
   */
  idFields?: string[]
  /** Fields to strip from the data before exporting and to ignore on import */
  excludeFields?: string[]
  /**
   * Custom serialise/deserialise/validate logic for specific fields.
   * Applied after id-flattening on download, and before id-unflattening on upload.
   */
  fieldTransformers?: Record<string, FieldTransformer>
  /**
   * Column names to include in a template file when the list is empty.
   * Without this, downloading an empty list does nothing.
   */
  templateFields?: string[]
  /** Example values per field shown as the first data row in the template. */
  templateExample?: Record<string, string>
  /**
   * Set of IDs that already exist — used to classify rows as new vs updated.
   * Taken from `loadAll` instead when that is given.
   */
  existingIds?: Set<string>
  /** Called with the processed rows after the user confirms the upload */
  onUpload: (rows: Record<string, unknown>[]) => Promise<void>
}

export function ImportExportButtons<T extends { id: string; name?: string }>({
  data = [],
  loadAll,
  filename,
  idFields = [],
  excludeFields = [],
  fieldTransformers = {},
  templateFields,
  templateExample,
  existingIds,
  onUpload,
}: ImportExportButtonsProps<T>) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFormat, setPendingFormat] = useState<'csv' | 'excel' | null>(null)
  const [uploadRows, setUploadRows] = useState<UploadRow[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  /** The full list — fetched when the page only holds a slice of it. */
  const resolveAll = async () => (loadAll ? await loadAll() : data)

  // ── Download ────────────────────────────────────────────────────────────────

  const handleDownload = async (format: 'csv' | 'excel') => {
    setBusy(true)
    try {
      const all = await resolveAll()
      const flat = all.map((item) => {
        const obj = flattenIds(item as unknown as Record<string, unknown>)
        for (const f of excludeFields) delete obj[f]
        return obj
      })
      if (format === 'csv') exportToCSV(flat, filename, fieldTransformers, templateFields, templateExample)
      else await exportToExcel(flat, filename, fieldTransformers, templateFields, templateExample)
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  // ── Upload ──────────────────────────────────────────────────────────────────

  const triggerUpload = (format: 'csv' | 'excel') => {
    setPendingFormat(format)
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset so the same file can be re-selected
    e.target.value = ''
    if (!file || !pendingFormat) return

    setBusy(true)
    try {
      const parsed =
        pendingFormat === 'csv'
          ? await parseCSV(file, fieldTransformers)
          : await parseExcel(file, fieldTransformers)

      if (!parsed.length) {
        toast.error(t('importExport.emptyFile'))
        return
      }

      // Validate transformer fields before showing the confirm modal
      const errors = validateRows(parsed, fieldTransformers)
      if (errors.length) {
        toast.error(t('importExport.validationError'), {
          description: errors.slice(0, 5).join('\n') + (errors.length > 5 ? `\n…and ${errors.length - 5} more` : ''),
          duration: 8000,
        })
        return
      }

      const all = await resolveAll()
      const knownIds = existingIds ?? new Set(all.map((item) => item.id))
      const existingByIdFlat = new Map(
        all.map((item) => {
          const obj = flattenIds(item as unknown as Record<string, unknown>)
          for (const f of excludeFields) delete obj[f]
          return [item.id, obj]
        })
      )

      const rows: UploadRow[] = parsed.map((obj) => {
        const id = typeof obj.id === 'string' && obj.id.trim() ? obj.id.trim() : generateId()
        const processed = unflattenIds({ ...obj, id }, idFields)
        const isNew = !knownIds.has(id)
        return {
          id,
          name: typeof obj.name === 'string' ? obj.name : undefined,
          isNew,
          raw: processed,
          before: isNew ? undefined : existingByIdFlat.get(id),
        }
      })

      setUploadRows(rows)
      setConfirmOpen(true)
    } catch (err) {
      toast.error(t('importExport.parseError'), {
        description: (err as Error).message,
        duration: 5000,
      })
    } finally {
      setBusy(false)
    }
  }

  const handleConfirm = async (rows: Record<string, unknown>[]) => {
    try {
      await onUpload(rows)
      setConfirmOpen(false)
      toast.success(t('importExport.uploadSuccess'), {
        description: t('importExport.uploadSuccessDetail', { count: rows.length }),
        duration: 3000,
      })
    } catch (err) {
      toast.error(t('importExport.uploadError'), {
        description: (err as Error).message,
        duration: 5000,
      })
    }
  }

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Download dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={busy}>
            <Download className="size-4" />
            {t('importExport.download')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleDownload('excel')}>
            <FileSpreadsheet className="size-4" />
            {t('importExport.downloadExcel')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleDownload('csv')}>
            <FileText className="size-4" />
            {t('importExport.downloadCSV')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Upload dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={busy}>
            <Upload className="size-4" />
            {t('importExport.upload')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => triggerUpload('excel')}>
            <FileSpreadsheet className="size-4" />
            {t('importExport.uploadExcel')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => triggerUpload('csv')}>
            <FileText className="size-4" />
            {t('importExport.uploadCSV')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation modal */}
      <UploadConfirmModal
        rows={uploadRows}
        open={confirmOpen}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  )
}
